// ═══════════════════════════════════════════════════════════════════════════
// Living Engine Hooks
// packages/@forgetrack/engine/src/hooks.ts
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';

/**
 * Animates a number from 0 to `target` over `duration` ms, starting after
 * `delay` ms. Returns the current display value.
 */
export function useCounter(target: number, duration = 800, delay = 0): number {
  const [value, setValue] = useState(0);
  const startTime = useRef<number | null>(null);
  const rafId = useRef<number>(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const animate = (timestamp: number) => {
        if (!startTime.current) startTime.current = timestamp;
        const elapsed = timestamp - startTime.current;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(Math.round(target * eased));
        if (progress < 1) {
          rafId.current = requestAnimationFrame(animate);
        }
      };
      rafId.current = requestAnimationFrame(animate);
    }, delay);

    return () => {
      clearTimeout(timeout);
      cancelAnimationFrame(rafId.current);
    };
  }, [target, duration, delay]);

  return value;
}

/** Returns a boolean that toggles every `intervalMs`. Use for pulsing "LIVE" indicators. */
export function useLiveTick(intervalMs = 2000): boolean {
  const [tick, setTick] = useState(true);
  useEffect(() => {
    const id = setInterval(() => setTick(t => !t), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return tick;
}

/** Returns the current UTC time string, updated every second. */
export function useClock(): string {
  const [time, setTime] = useState(() => new Date().toISOString().slice(11, 19) + ' UTC');
  useEffect(() => {
    const id = setInterval(() => {
      setTime(new Date().toISOString().slice(11, 19) + ' UTC');
    }, 1000);
    return () => clearInterval(id);
  }, []);
  return time;
}

/** Generates sparkline-style random data points for decorative charts. */
export function useSparklineData(count = 12, min = 20, max = 100): { x: number; y: number }[] {
  const [data] = useState(() =>
    Array.from({ length: count }, (_, i) => ({
      x: i,
      y: Math.round(min + Math.random() * (max - min)),
    })),
  );
  return data;
}

/** Simple debounce hook for search inputs. */
export function useDebounce<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/** Returns whether an element is in view (for scroll-triggered animations). */
export function useInView(
  ref: React.RefObject<HTMLElement | null>,
  options?: IntersectionObserverInit,
): boolean {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1, ...options },
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, options]);
  return inView;
}
